import { useMemo } from "react";
import {
  Coupon,
  Currency,
  Product,
  ProductAvailabilityType,
  ProductPrice,
} from "../../../../../../graphql/operations";
import ReservationOffer from "../../ReservationOffer";
import styles from "../CheckoutSummary.module.css";
import OrderLineItem from "./OrderLine";

export type CheckoutTotalProps = {
  productId: string;
  lineItems: Array<{
    typeId: string;
    quantity: number;
    price: number;
  }>;
  productPrices: ProductPrice[];
  currencyCode: Currency;
  couponCode?: string | null;
  privatePrice?: number;
  coupon: Coupon;
} & Pick<Product, "availabilityType">;

export default function CheckoutTotal({
  productId,
  currencyCode,
  lineItems,
  productPrices,
  couponCode,
  coupon,
  privatePrice,
  availabilityType,
  children,
}: React.PropsWithChildren<CheckoutTotalProps>) {
  const tableItems = useMemo(
    () => ({
      "number of guests": productPrices.map((price) => {
        const selected = lineItems.find((item) => item.typeId === price.id);

        return {
          typeId: price.id,
          label: price.translation.name,
          quantity: selected ? selected.quantity : 0,
          unitAmount: price.amount,
        };
      }),
      ...(privatePrice !== undefined
        ? {
            extras: [
              {
                typeId: "privatePrice",
                label:
                  availabilityType === ProductAvailabilityType.PublicAndPrivate
                    ? "Private group price"
                    : "Group charge",
                unitAmount: privatePrice,
              },
            ],
          }
        : {}),
    }),
    [lineItems, privatePrice, productPrices, availabilityType]
  );

  return (
    <>
      <ul>
        {Object.entries(tableItems).map(([header, items]) => (
          <li key={header} className={styles.SumItemRow}>
            <div className={styles.CheckoutSummaryTerm}>{header}</div>
            <div
              style={{
                width: "100%",
              }}
            >
              {items.map((item) => {
                return (
                  (item.quantity ?? 1) > 0 && (
                    <div key={item.typeId}>
                      <OrderLineItem
                        quantity={item.quantity}
                        label={item.label}
                        unitAmount={item.unitAmount}
                        currencyCode={currencyCode}
                        hideFree={
                          availabilityType === ProductAvailabilityType.Private
                        }
                      />
                    </div>
                  )
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      {children}
      <ReservationOffer
        couponCode={couponCode}
        lineItems={lineItems.map((d) => ({
          priceTypeId: d.typeId,
          price: d.price,
          quantity: d.quantity,
        }))}
        productId={productId}
        currencyCode={currencyCode}
        coupon={coupon}
        privatePrice={privatePrice}
      />
    </>
  );
}
